import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { viewportOnce, stagger, staggerItem } from '../motion';
import { experience, Experience as ExperienceItem } from '../content';
import SectionHeading from './SectionHeading';

const Meta: React.FC<{ label: string }> = ({ label }) => (
  <Box
    component="span"
    sx={{
      fontFamily: (t) => t.typography.button.fontFamily,
      fontSize: '0.7rem',
      color: 'text.secondary',
      px: 1,
      py: 0.4,
      borderRadius: 1,
      border: (t) => `1px solid ${t.palette.divider}`,
    }}
  >
    {label}
  </Box>
);

const Entry: React.FC<{ item: ExperienceItem }> = ({ item }) => (
  <Box sx={{ position: 'relative', pl: { xs: 4, md: 5 }, pb: { xs: 6, md: 7 } }}>
    {/* Rail dot */}
    <Box
      sx={{
        position: 'absolute',
        left: { xs: -5, md: -5 },
        top: 6,
        width: 11,
        height: 11,
        borderRadius: '50%',
        backgroundColor: item.current ? 'primary.main' : 'background.default',
        border: (t) =>
          `2px solid ${item.current ? t.palette.primary.main : t.palette.divider}`,
        boxShadow: (t) => (item.current ? `0 0 0 4px ${t.palette.primary.main}22` : 'none'),
        zIndex: 1,
      }}
    />

    {/* Period + duration */}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
      <Typography
        sx={{
          fontFamily: (t) => t.typography.button.fontFamily,
          fontSize: '0.75rem',
          color: 'primary.main',
        }}
      >
        {item.period}
      </Typography>
      {item.duration && (
        <Typography
          sx={{
            fontFamily: (t) => t.typography.button.fontFamily,
            fontSize: '0.7rem',
            color: 'text.secondary',
          }}
        >
          · {item.duration}
        </Typography>
      )}
    </Box>

    <Typography variant="h4" component="h3" sx={{ color: 'text.primary', mb: 0.25 }}>
      {item.role}
    </Typography>
    <Typography
      sx={{
        fontFamily: (t) => t.typography.button.fontFamily,
        fontSize: '0.8rem',
        color: 'text.secondary',
        mb: 2,
      }}
    >
      {item.company}
      {item.type ? ` · ${item.type}` : ''} · {item.location}
    </Typography>

    {item.description && (
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640, mb: item.bullets ? 1.5 : 0 }}>
        {item.description}
      </Typography>
    )}

    {item.bullets && (
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
        {item.bullets.map((b) => (
          <Typography
            key={b}
            component="li"
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 640, mb: 0.5 }}
          >
            {b}
          </Typography>
        ))}
      </Box>
    )}

    {item.tags && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 2 }}>
        {item.tags.map((t) => (
          <Meta key={t} label={t} />
        ))}
      </Box>
    )}
  </Box>
);

const Experience: React.FC = () => (
  <Box component="section" id="experience" sx={{ py: { xs: 10, md: 18 }, position: 'relative' }}>
    <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
      <SectionHeading
        index="03"
        eyebrow="experience"
        title="Where I've worked"
        subtitle="A decade of shipping software — from fintech in Accra to remote teams across the world."
      />

      <motion.div
        variants={stagger(0.1)}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        {/* Vertical rail */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: 820,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: '1px',
              backgroundColor: 'divider',
            },
          }}
        >
          {experience.map((item) => (
            <motion.div key={`${item.company}-${item.period}`} variants={staggerItem}>
              <Entry item={item} />
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </Container>
  </Box>
);

export default Experience;
